import React, { memo, useCallback, useRef, useMemo, useState } from 'react';
import cls from 'clsx';
import { noop } from '@/utils/misc';
import { useListKeyboardNav } from '@/hooks/useListKeyboardNav';
import styles from './select.module.css';
import {
  ISelectProps,
  ISingleSelectProps,
  IBasicSingleSelectProps,
  SelectOptionType,
  SelectValueType
} from './Select.types';
import Popper from '../Popper';
import Option from './Option';
import VirtualScroll from '../VirtualScroll';
import { LIST_HEIGHT, LIST_SIZE } from './Select.constants';
import { ReactComponent as Down } from '@/assets/icons/caret-down.svg';
import { ReactComponent as Clear } from '@/assets/icons/clear.svg';

const isBasicProps = (
  props: ISingleSelectProps
): props is IBasicSingleSelectProps => 'options' in props;

const SingleSelect: React.FC<ISingleSelectProps> = props => {
  const {
    value = '',
    onSelect = noop,
    className = '',
    disabled = false,
    clearable = false,
    invalid = false,
    virtualScroll = false,
    placement = 'bottom',
    withArrow = false,
    withinPortal = true,
    sameWidth = true
  } = props;
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const clearRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const onClose = useCallback(() => setVisible(false), []);
  const [handleKeyDown, setPopperRef] = useListKeyboardNav(visible, onClose);

  const handleClick = useCallback(
    (optionValue: SelectValueType) => {
      onSelect(optionValue);
      setVisible(false);
    },
    [onSelect]
  );

  const mergedOptions: Array<SelectOptionType> = useMemo(() => {
    if (isBasicProps(props)) {
      return props.options;
    } else {
      const { children } = props;
      return (
        React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            const { value, label } = child.props as SelectOptionType;
            return {
              label,
              value
            };
          }
        }) || []
      );
    }
  }, [props]);

  const selectedElement = useMemo(() => {
    if (isBasicProps(props)) {
      const label = mergedOptions.find(option => option.value === value)?.label;
      return <span className={styles.text}>{label}</span>;
    } else {
      const { selected = null } = props;
      return selected;
    }
  }, [mergedOptions, value, props]);

  const showClear = useMemo(() => clearable && !!value, [clearable, value]);

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onSelect('');
      buttonRef.current?.focus();
    },
    [onSelect]
  );

  const toggleVisible = useCallback(() => {
    setVisible(visible => !visible);
  }, []);

  const selectOptions = useMemo(() => {
    return mergedOptions;
  }, [mergedOptions]);

  const listView = useMemo(() => {
    if (isBasicProps(props)) {
      const itemData = selectOptions.map(option => (
        <Option
          key={option.value.toString()}
          value={option.value}
          onClick={handleClick}
          selected={option.value === value}
        >
          <button
            disabled={option.disabled}
            className={cls(styles.menuItemButton, {
              [styles.selected]: option.value === value
            })}
          >
            {option.label}
          </button>
        </Option>
      ));

      if (virtualScroll) {
        return (
          <VirtualScroll
            itemCount={selectOptions.length}
            height={LIST_HEIGHT}
            childHeight={LIST_SIZE}
            itemData={itemData}
          />
        );
      } else {
        return itemData;
      }
    } else {
      const { children } = props;
      return React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          const { value } = child.props;
          if (selectOptions.some(option => option.value === value)) {
            return React.cloneElement(child as React.ReactElement, {
              onClick: handleClick
            });
          }
        }
      });
    }
  }, [props, handleClick, value, selectOptions, virtualScroll]);

  return (
    <>
      <div
        ref={setReferenceElement}
        className={cls(
          styles.ref,
          {
            [styles.disabled]: disabled,
            [styles.invalid]: invalid
          },
          className
        )}
        onClick={toggleVisible}
      >
        <button
          type="button"
          role="combobox"
          aria-expanded={visible}
          aria-haspopup="listbox"
          ref={buttonRef}
          className={styles.button}
          disabled={disabled}
        >
          {selectedElement}
        </button>
        <div className={styles.icon}>
          <Down className={styles.down} />
          {showClear && (
            <button
              type="button"
              ref={clearRef}
              onClick={handleClear}
              className={styles.clear}
            >
              <Clear className={styles.clearIcon} />
            </button>
          )}
        </div>
      </div>
      {!disabled && (
        <Popper
          referenceElement={referenceElement}
          placement={placement}
          withArrow={withArrow}
          withinPortal={withinPortal}
          sameWidth={sameWidth}
          visible={visible}
          onClose={onClose}
        >
          <div
            className={styles.select}
            ref={setPopperRef}
            onKeyDown={handleKeyDown}
          >
            <div className={styles.menu} role="listbox">
              {listView}
            </div>
          </div>
        </Popper>
      )}
    </>
  );
};

const Select: React.FC<ISelectProps> = props => {
  return <SingleSelect {...props} />;
};

export default memo(Select);
